import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiGetAll, getOtherTask, updateOtherTask } from '../api';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annually' },
];

export default function OtherTaskDetailPage() {
  const { id } = useParams();
  const [task, setTask] = useState(null);
  const [client, setClient] = useState(null);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    getOtherTask(id)
      .then(setTask)
      .catch((e) => setError(e.message || 'Failed to load task'));
  }, [id]);

  const clientId = task?.client;

  // The point of this page: the client's master file is right here, so working
  // a bookkeeping task doesn't mean searching the client up separately. The
  // master file is a SharePoint document -- ClientZone links to it and stores
  // no credentials of its own.
  useEffect(() => {
    if (!clientId) return;
    apiGet(`/api/clients/${clientId}/`).then(setClient).catch(() => setClient(null));
    apiGetAll(`/api/client-files/?client=${clientId}`)
      .then((f) => setFiles(Array.isArray(f) ? f : []))
      .catch(() => setFiles([]));
  }, [clientId]);

  const onRecurrenceChange = async (value) => {
    const previous = task.recurrence;
    setTask((t) => ({ ...t, recurrence: value }));   // optimistic
    try {
      await updateOtherTask(task.id, { recurrence: value });
      setError(null);   // clear a stale failure from an earlier save
    } catch (e) {
      setTask((t) => ({ ...t, recurrence: previous }));   // revert -- the server never stored this
      setError('Could not save the recurrence. Reload and try again.');
    }
  };

  if (error && !task) return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;
  if (!task) return <div style={{ padding: 24 }}>Loading…</div>;

  const typeLabel = task.task_type_name || (task.task_type ? `Type #${task.task_type}` : '—');
  const statusLabel = task.status_name || (task.status ? `Status #${task.status}` : '—');

  return (
    <div style={{ padding: 24 }}>
      <Link to="/tasks" style={{ color: '#6b7280', textDecoration: 'none' }}>← Tasks</Link>
      <h1 style={{ marginTop: 8 }}>{task.name || `OtherTask ${task.id}`}</h1>
      <div style={{ color: '#6b7280' }}>
        Other Task
        {task.client ? (
          <>
            {' — '}
            <Link to={`/clients/${task.client}`} style={{ color: '#059669' }}>
              {task.client_name || `Client #${task.client}`}
            </Link>
          </>
        ) : null}
      </div>

      {error ? (
        <div style={{ marginTop: 12, color: '#dc2626', fontSize: 13 }}>{error}</div>
      ) : null}

      <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <Field label="Type" value={typeLabel} />
        <Field label="Status" value={statusLabel} />
        <Field label="Due Date" value={task.due_date} />
        <Field label="Created" value={(task.created_at || '').slice(0, 10)} />
        <div>
          <Label>Repeats</Label>
          <select
            id="recurrence"
            aria-label="Repeats"
            value={task.recurrence || 'none'}
            onChange={(e) => onRecurrenceChange(e.target.value)}
            style={{ marginTop: 2, padding: '4px 6px', fontSize: 14 }}
          >
            {RECURRENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Closing a repeating task creates the next one automatically.
          </div>
        </div>
      </div>

      {task.client ? (
        <div style={{ marginTop: 32, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
          <section>
            <SectionHeading>Client</SectionHeading>
            {client ? (
              <div style={{ display: 'grid', gap: 8 }}>
                <Field label="Contact" value={client.contact} />
                <Field label="Email" value={client.email} />
                <Field label="Phone" value={client.phone} />
                <Field label="Address" value={client.full_address || client.address} />
              </div>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>Loading client…</div>
            )}
          </section>

          <section>
            <SectionHeading>Master file</SectionHeading>
            {files.length ? (
              <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
                {files.map((f) => {
                  const href = f.file_url || f.sharepoint_url || null;
                  return (
                    <li key={f.id}>
                      {href ? (
                        <a href={href} target="_blank" rel="noreferrer" style={{ color: '#059669' }}>
                          {f.name}
                        </a>
                      ) : (
                        f.name
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div style={{ color: '#6b7280', fontSize: 14 }}>
                No files linked to this client yet.
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}

function Label({ children }) {
  return (
    <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.05em',
                 color: '#374151', margin: '0 0 12px' }}>
      {children}
    </h2>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <Label>{label}</Label>
      <div style={{ fontSize: 14, marginTop: 2 }}>{value || '—'}</div>
    </div>
  );
}
